import React from 'react';
import ReactDOM from 'react-dom';
import constants from '../../../qti/Constants';
import sessionReader from '../../../store/readHelpers/SessionReader';

import { reportError } from '../../../services/ErrorReporting';

import MultimediaDisplay from './multimedia/MultimediaDisplay';
import EmbedDisplay from './embed/EmbedDisplay';
import ImageDisplay from './image/ImageDisplay';
import LinkDisplay from './link/LinkDisplay';
import PlaceholderDisplay from './placeholder/PlaceholderDisplay';
import MultimediaPlaceholderDisplay from './multimedia/MultimediaPlaceholderDisplay';

const { mediaType } = constants;

function getComponent(type) {
    const usePlaceholder = sessionReader.isViewMediaPrint() || sessionReader.isViewMediaPdf();
    if (type === mediaType.AUDIO || type === mediaType.VIDEO) {
        return usePlaceholder ? MultimediaPlaceholderDisplay : MultimediaDisplay;
    }

    if (type === mediaType.EMBED) {
        return usePlaceholder ? PlaceholderDisplay : EmbedDisplay;
    }

    if (type === mediaType.IMAGE) {
        return ImageDisplay;
    }

    if (type === mediaType.LINK) {
        return LinkDisplay;
    }
    reportError(
        new Error(`Media type ${type} is not supported`)
    );
    return PlaceholderDisplay;
}

function getImageModelForYouTubeEmbed(mediaModel) {
    const newModel = { ...mediaModel };
    const urlPath = mediaModel.url.split('?')[0];
    const urlParts = urlPath.split('/');
    const videoId = urlParts[urlParts.length - 1];

    newModel.url = `https://i.ytimg.com/vi/${videoId}/sddefault.jpg`;
    newModel.type = 'image';
    newModel.mimeType = 'image/jpeg';
    newModel.objectType = 'content_image';

    return newModel;
}

function isYouTubeEmbedUrl(url) {
    return url.indexOf('youtube.com') !== -1;
}

/**
 * Renders a Media component into the dom element with id as provided in model.target.
 * @param {MediaModel} model
 * @param services
 * @param {string} displayContext 'choiceOptions'
 * @param {object} mediaElement
 * @param {bool} isPreRender
 * @param {bool} lockedMedia Is media start locked
 * @return {Promise<*>}
 */
export async function showMedia(params) {
    const {
        displayContext,
        isInteraction = false,
        isPreRender = false,
        lockedMedia = false,
        mediaElement,
        model,
        onMediaPlayedCounterReset = null,
        onMediaPlayedToCompletion = null,
        questionObjectType,
        services,
        usePaperBasedImageDimensions = false,
        useMCDiscreteWithOneImageMaxWidth = false,
    } = params;

    return new Promise((resolve) => {
        let Media = getComponent(model.type);
        let modelToUse = model;

        if ((sessionReader.isViewMediaPrint() || sessionReader.isViewMediaPdf())
            && model.type === mediaType.EMBED
            && isYouTubeEmbedUrl(model.url)) {
            Media = ImageDisplay;
            modelToUse = getImageModelForYouTubeEmbed(model);
        }

        const mediaTarget = document.getElementById(modelToUse.target);
        if (!mediaTarget) {
            // It is valid that some media models do not have targets until we have fixed parsing the media models
            // that are only on the wanted language
            resolve(true);
            return;
        }

        const maxWidth = modelToUse.isMainMedia ? mediaTarget.clientWidth : undefined;
        const reportReady = (success) => {
            if (success) {
                return resolve(true);
            }

            throw new Error(`Media destined for target ${modelToUse.target} reported false.`);
        };

        ReactDOM.render(
            <Media
                displayContext={displayContext}
                isInteraction={isInteraction}
                isPreRender={isPreRender}
                lockedMedia={lockedMedia}
                maxWidth={maxWidth}
                mediaElement={mediaElement}
                model={modelToUse}
                onMediaPlayedCounterReset={onMediaPlayedCounterReset}
                onMediaPlayedToCompletion={onMediaPlayedToCompletion}
                questionObjectType={questionObjectType}
                reportReady={reportReady}
                services={services}
                usePaperBasedImageDimensions={usePaperBasedImageDimensions}
                useMCDiscreteWithOneImageMaxWidth={useMCDiscreteWithOneImageMaxWidth}
            />,
            mediaTarget
        );
    });
}

export function unmountMedia(target) {
    const mediaTarget = document.getElementById(target);
    if (mediaTarget) {
        return ReactDOM.unmountComponentAtNode(mediaTarget);
    }

    return undefined;
}
